/**
 * Learner-facing Open edX APIs: catalogue, course detail, enrolment, dashboard.
 *
 * Every endpoint here is one the platform already ships. Nothing is custom, and nothing
 * is proxied through a BFF — the browser calls the LMS on the tenant's own hostname, so
 * Open edX resolves the Site from the Host and applies its own tenant scoping.
 *
 * Shapes below were read off the live server, not from documentation. Where the platform
 * differs from what its docstrings claim, the comment says so — those differences are the
 * ones that cost a day when you hit them later.
 *
 * ---------------------------------------------------------------------------------
 * TENANCY. This is the part to get right, so it is stated once, here.
 *
 * The LIST endpoint is scoped by the server. `branding.get_visible_courses()` reads the
 * current Site's `course_org_filter` and will only return orgs on that list; an `?org=`
 * naming an org the Site does not own yields an empty page rather than that org's
 * courses. Verified live: on one tenant's host, `?org=<another tenant's org>` returns
 * count 0 while those courses plainly exist.
 *
 * The DETAIL endpoints are NOT scoped. `/api/courses/v1/courses/{id}` and
 * `/api/course_home/course_metadata/{id}` answer for any course from any host — verified
 * live, 200 for another tenant's course. So every read here is filtered again against
 * `config.tenant.orgs` before it reaches a page. Server scoping is the boundary; this is
 * the belt to its braces, and it is what stops a guessed course id rendering another
 * academy's course inside this one.
 * ---------------------------------------------------------------------------------
 */

import { openedx, ApiError } from './openedx';
import { getConfig } from './config';

/* ------------------------------------------------------------------ tenancy ----- */

/**
 * Orgs this academy owns, upper-cased for comparison.
 *
 * Case matters more than it looks. The list endpoint's docstring calls `org`
 * case-insensitive, but the Site guard in `branding.get_visible_courses` compares the
 * parameter to `course_org_filter` with a plain `in`, so a lower-case org silently
 * returns nothing. Verified on a live install: a lower-cased org returned 0 results while
 * the same org upper-cased returned the full catalogue. We therefore send orgs exactly as
 * configured and compare upper-cased.
 */
function tenantOrgs(): string[] {
  return getConfig().tenant.orgs;
}

/** Does this academy own the org? The org-level half of the answer. */
export function belongsToTenant(org: string | undefined | null): boolean {
  if (!org) return false;
  const mine = tenantOrgs().map((o) => o.toUpperCase());
  return mine.includes(org.toUpperCase());
}

/* ------------------------------------------------------------------ licences ----- */

/**
 * LICENSED COURSES — the second, per-course way a course belongs to this academy.
 *
 * `course_org_filter` is the only catalogue scope the platform has and it is ORG-level, so
 * a course cannot be shared between academies without handing over its whole org. The
 * platform console can therefore grant a single course to a tenant, stored on the tenant's
 * own site values and delivered here in the resolved config.
 *
 * WHICH MEANS THE LIST ENDPOINT CANNOT SEE THEM, and that is not a detail — it is the
 * whole reason this file needs extra work. `branding.get_visible_courses()` filters by the
 * Site's `course_org_filter`, so `?org=<a foreign org>` returns an empty page from this
 * hostname. Measured on this install: on one tenant's host, `?org=` naming the other
 * tenant's org returned `count: 0` while those three courses plainly existed, both
 * anonymously and as a superuser.
 *
 * The DETAIL endpoint has no such filter — measured 200, anonymously, for a course from an
 * org this host does not own — so a licensed course is fetched BY KEY, one request each,
 * and merged into the catalogue. That asymmetry is upstream's, and it is exactly why every
 * detail read in this file still has to be re-checked against the tenant afterwards.
 */
function licensedCourseIds(): string[] {
  return getConfig().tenant.licensedCourses ?? [];
}

/**
 * May this academy show this course at all? The full answer, org OR licence.
 *
 * Use this rather than `belongsToTenant` wherever a course ID is in hand. The id is
 * compared as a string against the stored keys, which are written in canonical form by the
 * console, so no key parsing happens in the browser.
 */
export function visibleToTenant(courseId: string, org: string | undefined | null): boolean {
  return belongsToTenant(org) || licensedCourseIds().includes(courseId);
}

/**
 * The organisation a course key belongs to. `course-v1:ORG+NUMBER+RUN`.
 *
 * Needed because several endpoints return a course key and no org — the certificates list is
 * the main one — and every surface that shows those has to narrow them to this academy.
 * Parsing lives here rather than in each page so the key format is written down once.
 */
export function orgOfCourseId(courseId: string): string | null {
  const body = courseId.includes(':') ? courseId.slice(courseId.indexOf(':') + 1) : courseId;
  return body.split('+')[0] || null;
}

/** `visibleToTenant` for a bare course key, with the org read off the key itself. */
export const courseIdVisibleToTenant = (courseId: string) =>
  visibleToTenant(courseId, orgOfCourseId(courseId));

/* ---------------------------------------------------------------- catalogue ----- */

/**
 * Course media, verbatim from `CourseSerializer`.
 *
 * `image.*` are ABSOLUTE urls on the LMS host; `course_image.uri` is relative. Both are
 * the same asset. We use the absolute form because the relative one is not on the dev
 * proxy's prefix list and would resolve to the SPA's own index.html.
 */
export interface CourseMedia {
  banner_image?: { uri: string | null; uri_absolute?: string | null };
  course_image?: { uri: string | null };
  course_video?: { uri: string | null };
  image?: { raw?: string; small?: string; large?: string };
}

/** `GET /api/courses/v1/courses/` result element. */
export interface CatalogueCourse {
  id: string;
  /** Duplicate of `id`. Present on both list and detail; `id` is the one to use. */
  course_id: string;
  name: string;
  number: string;
  org: string;
  short_description: string | null;
  /** Free text as typed in Studio, e.g. "4 hours per week". Frequently null. */
  effort: string | null;
  start: string | null;
  end: string | null;
  /** Pre-formatted start, or null when the author never set one. */
  start_display: string | null;
  /** 'string' | 'timestamp' | 'empty' — 'empty' means there is no real start date. */
  start_type: string;
  enrollment_start: string | null;
  enrollment_end: string | null;
  pacing: string;
  invitation_only: boolean;
  /**
   * True when catalog visibility is 'about' or 'none'.
   *
   * The list endpoint does NOT drop these — it returns them with the flag set, because
   * the catalogue permission it applies (`see_exists`) does not consider catalog
   * visibility at all. A catalogue that shows them is showing courses the author
   * deliberately unlisted, so we filter on it ourselves.
   */
  hidden: boolean;
  mobile_available: boolean;
  media: CourseMedia;
}

/** Detail adds the course's About page as raw HTML. */
export interface CourseDetail extends CatalogueCourse {
  overview: string | null;
}

/**
 * The list endpoint's pagination block.
 *
 * NOT the DRF default. The payload is `{ results, pagination }` rather than
 * `{ count, next, previous, results }`, so a client written against the usual shape
 * reads `count` as undefined and concludes the catalogue is empty.
 *
 * `next`/`previous` are absolute urls on the LMS host without the dev port, so they are
 * unusable as links — page through with `?page=` instead. Asking for a page past the
 * last one is a 404, not an empty page.
 */
export interface Pagination {
  next: string | null;
  previous: string | null;
  count: number;
  num_pages: number;
}

interface CourseListResponse {
  results: CatalogueCourse[];
  pagination: Pagination;
}

export interface CataloguePage {
  courses: CatalogueCourse[];
  /** Total across every org, for "N courses". Approximate while a search is active. */
  count: number;
  hasMore: boolean;
}

export interface CatalogueQuery {
  search?: string;
  page: number;
  pageSize: number;
}

/**
 * One page of the tenant's catalogue.
 *
 * ONE REQUEST, WITH NO `org` PARAMETER, and both halves of that are deliberate.
 *
 * This used to fan out one request per org and merge each licensed course in by key, because
 * `?org=` takes a single value and the list endpoint could not see a licence. Neither is true
 * any more: the platform's own site scoping now applies the licence as well as the org filter
 * (`openedx_thinlms_tenancy.licences` wraps `branding.get_visible_courses`), so asking without
 * `org=` returns exactly what this academy may show — its own orgs' courses plus anything
 * licensed to it. Measured per host: the owning academy's 3 courses, and 2 on an academy that
 * owns no courses at all and holds 2 licences.
 *
 * Passing `?org=` would now UNDO that. An explicit org means "only this organisation", which
 * the server honours, so a licensed course from a different org would be filtered back out —
 * the fan-out that used to be necessary would today be the thing breaking it.
 *
 * `hidden` and `visibleToTenant` still run over the results. The server is the boundary; these
 * are the belt to its braces, and `hidden` is a real filter rather than defence — the list
 * endpoint returns unlisted courses with the flag set, because the permission it checks does
 * not consider catalogue visibility at all.
 */
export async function listCatalogue(query: CatalogueQuery): Promise<CataloguePage> {
  const params = new URLSearchParams({
    page: String(query.page),
    page_size: String(query.pageSize),
  });
  if (query.search) params.set('search_term', query.search);

  let page: CourseListResponse;
  try {
    page = await openedx.get<CourseListResponse>(`/api/courses/v1/courses/?${params}`);
  } catch (error) {
    // Past the last page. The endpoint 404s rather than returning an empty page.
    if (error instanceof ApiError && error.status === 404) {
      return { courses: [], count: 0, hasMore: false };
    }
    throw error;
  }

  const courses = page.results.filter(
    (course) => !course.hidden && visibleToTenant(course.id, course.org),
  );

  return {
    courses: courses.sort((a, b) => a.name.localeCompare(b.name)),
    count: page.pagination.count,
    hasMore: page.pagination.next !== null,
  };
}

/**
 * Full course record, including the About page HTML.
 *
 * Throws `NotOurCourse` when the id belongs to another academy. The server answers for
 * any course from any host (see the header), so this is the only thing standing between
 * a pasted course id and one tenant rendering another's course.
 */
export class NotOurCourse extends Error {
  constructor(readonly org: string) {
    super('That course belongs to a different academy.');
    this.name = 'NotOurCourse';
  }
}

export async function getCourse(courseId: string): Promise<CourseDetail> {
  const course = await openedx.get<CourseDetail>(
    `/api/courses/v1/courses/${encodeURIComponent(courseId)}`,
  );
  // `visibleToTenant`, not `belongsToTenant`: a licensed course is legitimately not ours by
  // org, and refusing it here would make the course visible in the catalogue and unopenable.
  if (!visibleToTenant(course.id, course.org)) throw new NotOurCourse(course.org);
  return course;
}

/* ----------------------------------------------------------- course about ------ */

/**
 * The structured About record, from our own tenancy app.
 *
 * WHY THIS EXISTS AT ALL, since every other read in this file is a stock platform API.
 *
 * The fields a course-about page is made of — what you'll learn, who teaches it, how it
 * is graded, the syllabus — are all in Open edX, on `CourseDetails` and on the course
 * block's `grading_policy`. NONE of them reach a learner. `/api/courses/v1/courses/{id}`
 * returns `overview`, a blob of author HTML, and on a course nobody has edited that blob
 * is the platform's default About TEMPLATE: "About This Course / Requirements / Course
 * Staff / Frequently Asked Questions", filled with instructions to the author. It renders
 * as a finished page describing a course that does not exist. The outline endpoint the
 * learning MFE uses, `/api/course_home/outline/{id}`, 403s unless you are enrolled — no
 * use on a page whose entire job is to convince someone to enrol.
 *
 * So there is one endpoint of ours, and it is the only one on the learner side:
 *
 *     GET /api/tenant/v1/courses/{id}/about      anonymous, scoped by Host
 *
 * It is the only course read in this file that does NOT need `belongsToTenant` after it.
 * The server resolves the tenant from the request Host and 404s a course outside that
 * tenant's orgs, which is the guarantee the stock detail endpoints do not make (see the
 * header). The check is still cheap, so the caller keeps it — but here it is a second
 * lock on a door that is already locked, not the only one.
 */

/** A unit's kind, used to pick its icon. `null` when the unit holds nothing typed. */
export type UnitKind = 'video' | 'quiz' | 'assignment' | 'discussion' | 'reading' | null;

export interface AboutUnit {
  title: string;
  kind: UnitKind;
}

export interface AboutSubsection {
  title: string;
  graded: boolean;
  /** The grading-policy type this counts toward, e.g. 'Homework'. Null when ungraded. */
  assignmentType: string | null;
  /**
   * When this opens, or null if it is already open.
   *
   * A subsection that has not opened yet returns `unitCount` but an EMPTY `units` — the
   * shape of the course is public, its unreleased content is not. Rendering has to
   * handle a section with a count and no rows, which is the honest state, not a bug.
   */
  opens: string | null;
  unitCount: number;
  units: AboutUnit[];
}

export interface AboutSection {
  title: string;
  opens: string | null;
  subsections: AboutSubsection[];
}

export interface AboutOutline {
  sections: AboutSection[];
  sectionCount: number;
  subsectionCount: number;
  unitCount: number;
}

export interface AboutGrader {
  type: string;
  shortLabel: string | null;
  /** A PERCENTAGE, converted server-side from the stored fraction of one. */
  weight: number;
  minCount: number;
  dropCount: number;
  /** Graded subsections of this type that actually exist in the public outline. */
  subsections: number;
}

export interface AboutGrading {
  graders: AboutGrader[];
  /** Lowest cutoff, as a percentage — every band above it also passes. Null if unset. */
  passMark: number | null;
  cutoffs: Record<string, number>;
  /**
   * The graders' weights added up. NOT necessarily 100: the platform does not enforce
   * that a policy totals correctly, so a course can grade against a broken scale for a
   * whole run. Shown rather than assumed.
   */
  totalWeight: number;
}

export interface AboutInstructor {
  name: string;
  title: string;
  organization: string;
  image: string;
  bio: string;
}

export interface CourseAbout {
  courseId: string;
  org: string;
  number: string;
  run: string;
  title: string;
  subtitle: string;
  duration: string;
  effort: string;
  language: string;
  shortDescription: string;
  /** The author's About HTML. See the note above before rendering it. */
  overview: string;
  /** "What you'll learn", one bullet per line as typed in Studio. Often empty. */
  learningInfo: string[];
  instructors: AboutInstructor[];
  prerequisites: { courseId: string; title: string }[];
  /** Null when the course has no graded content — then there is nothing true to say. */
  grading: AboutGrading | null;
  outline: AboutOutline;
  /**
   * Whether finishing this course awards a certificate at all.
   *
   * Ours, not the platform's, and it exists because nothing else answers it for a learner.
   * `/api/course_home/v1/progress/{course}/` returns `certificate_data: null` for BOTH "this
   * course has no certificate" and "you have not earned it yet" — measured on this box,
   * where PATHTEST Course A reads 100% content-complete with `certificate_data: null` and no
   * certificate configuration. Every certificate surface checks this before promising
   * anything.
   *
   * Optional on the type so an older backend simply renders no certificate section, rather
   * than the SPA showing one on the strength of a missing field.
   */
  awardsCertificate?: boolean;
}

export const getCourseAbout = (courseId: string) =>
  openedx.get<CourseAbout>(
    `/api/tenant/v1/courses/${encodeURIComponent(courseId)}/about`,
  );

/**
 * Phrases that only ever appear in Open edX's UNTOUCHED About template.
 *
 * Studio seeds `overview` with a worked example — "About This Course / Requirements /
 * Course Staff / Frequently Asked Questions" — whose body is instructions addressed to
 * the author, plus two placeholder staff members with placeholder portraits. It is
 * valid HTML and it renders beautifully, which is the problem: a page that prints it
 * looks like a finished course description and is in fact a page telling a stranger to
 * "include your long course description here".
 *
 * These strings are the instructions themselves, so they cannot survive an author
 * actually writing the section they sit in. Matching any one of them means nobody has
 * touched this field.
 */
const OVERVIEW_PLACEHOLDERS = [
  'Include your long course description here',
  'This is paragraph 2 of the long course description',
  'Biography of instructor/staff member',
  'Add information about the skills and knowledge students need',
];

/**
 * The About HTML if a human wrote it, or null if it is still the stock template.
 *
 * Deliberately one-way: this only ever DEMOTES the platform's placeholder to "nothing
 * here". An author who edits half the template keeps whatever they wrote, because the
 * page's fallback for null is to say the description is missing — which is true — while
 * the alternative is to publish lorem ipsum as if it were the course.
 */
export function authoredOverview(html: string | null | undefined): string | null {
  const text = (html ?? '').trim();
  if (!text) return null;
  return OVERVIEW_PLACEHOLDERS.some((phrase) => text.includes(phrase)) ? null : text;
}

/* ------------------------------------------------------------ learner view ------ */

/**
 * `GET /api/course_home/course_metadata/{id}`.
 *
 * Answers 200 for ANONYMOUS visitors too, which is what makes it usable on a public
 * about page: `is_enrolled` comes back false and `course_access` explains why the
 * courseware is shut. It is the cheapest honest source for "am I enrolled" — cheaper
 * than the enrolment list, and correct for staff, who have access without an enrolment.
 */
export interface CourseMetadata {
  course_id: string;
  title: string;
  org: string;
  number: string;
  start: string | null;
  is_enrolled: boolean;
  is_staff: boolean;
  is_self_paced: boolean;
  username: string | null;
  /**
   * Whether the courseware itself is open, and why not when it isn't.
   *
   * `error_code` values seen live: 'course_not_started', 'enrollment_required',
   * 'authentication_required'. `user_message` is already written for a learner, so it is
   * shown as-is rather than re-worded.
   */
  course_access: {
    has_access: boolean;
    error_code: string | null;
    user_message: string | null;
    developer_message: string | null;
  };
  /** Enrollable tracks. A course with only 'audit' is free to join. */
  course_modes: { slug: string; name: string }[];
}

export const getCourseMetadata = (courseId: string) =>
  openedx.get<CourseMetadata>(
    `/api/course_home/course_metadata/${encodeURIComponent(courseId)}`,
  );

/* -------------------------------------------------------------- enrolment ------ */

/** `GET|POST /api/enrollment/v1/enrollment` element. A bare array — not paginated. */
export interface Enrolment {
  created: string;
  mode: string;
  is_active: boolean;
  user: string;
  course_details: {
    course_id: string;
    course_name: string;
    course_start: string | null;
    course_end: string | null;
    enrollment_start: string | null;
    enrollment_end: string | null;
    invite_only: boolean;
    pacing_type: string;
    course_modes: { slug: string; name: string; min_price: number }[];
  };
}

export const listEnrolments = () => openedx.get<Enrolment[]>('/api/enrollment/v1/enrollment');

/**
 * Enrol the signed-in user.
 *
 * `mode` is deliberately omitted. The server picks the course's default track, and a
 * learner posting anything other than audit/honor is refused with 403 — so naming a mode
 * can only make this fail. Re-posting an existing enrolment is a 200 with the existing
 * record, not an error, which makes the call safely idempotent.
 */
export const enrol = (courseId: string) =>
  openedx.post<Enrolment>('/api/enrollment/v1/enrollment', {
    course_details: { course_id: courseId },
  });

/**
 * Turn an enrolment failure into something a learner can act on.
 *
 * Worth knowing before reading this: the API does NOT distinguish the three interesting
 * refusals. `CourseEnrollmentClosedError` and `CourseEnrollmentFullError` are both
 * subclasses of `CourseEnrollmentError`, and the view's catch-all for that class answers
 * 400 with one generic sentence — "An error occurred while creating the new course
 * enrollment for user '…' in course '…'". Showing that to a learner tells them nothing.
 *
 * So the reason is reconstructed from the course record, which does carry the three
 * facts the server checked (`invitation_only`, `enrollment_start`, `enrollment_end`),
 * and "full" is what is left when neither of those explains it. The distinct statuses
 * the API does report are used directly.
 */
export function explainEnrolmentFailure(error: unknown, course: CatalogueCourse | undefined): string {
  if (!(error instanceof ApiError)) {
    return 'Your browser could not reach the academy. Check your connection and try again — you have not been enrolled.';
  }

  const message =
    typeof (error.body as { message?: unknown } | null)?.message === 'string'
      ? (error.body as { message: string }).message
      : null;

  if (error.status === 401) {
    return 'Your session has expired. Sign in again and the enrolment will go through.';
  }
  if (error.status === 403) {
    // Reached when the course's own enrolment rules refuse, or when a paid track was
    // requested. We never request a track, so in practice this is the former.
    return message ?? 'This course is not open for you to join.';
  }

  if (error.status === 400) {
    if (course?.invitation_only) {
      return 'This course is invitation only. Ask the course team to invite you, then enrol from this page.';
    }
    const now = Date.now();
    const opens = course?.enrollment_start ? Date.parse(course.enrollment_start) : null;
    const closes = course?.enrollment_end ? Date.parse(course.enrollment_end) : null;
    if (opens !== null && opens > now) {
      return `Enrolment has not opened yet. It opens on ${formatDate(course?.enrollment_start)}.`;
    }
    if (closes !== null && closes < now) {
      return `Enrolment closed on ${formatDate(course?.enrollment_end)}. Ask the course team whether it will run again.`;
    }
    if (message?.includes('found for enrollment')) {
      return 'That course is no longer available. It may have been unpublished since this page loaded.';
    }
    // Nothing on the course record explains it, and the server will not say more.
    return 'The course could not accept another learner — it has most likely reached its enrolment limit. Contact the course team if you think that is wrong.';
  }

  return `The academy could not complete the enrolment (error ${error.status}). Nothing has changed, so it is safe to try again.`;
}

/* -------------------------------------------------------------- dashboard ------ */

/**
 * `GET /api/learner_home/init` — everything the dashboard needs in one call.
 *
 * Chosen over the enrolment list because it already joins in the course record, the run
 * dates, progress state and a resume link; the enrolment list would need an extra detail
 * fetch per course to render the same card.
 *
 * It is NOT tenant-scoped: it returns every enrolment the person holds, on any academy.
 * `courseProvider.name` carries the org, so the filter belongs here — see `myCourses`.
 *
 * Field names are camelCase in this API and snake_case in every other one above. That is
 * the platform's inconsistency, not a typo.
 */
export interface LearnerCourse {
  course: {
    courseName: string;
    courseNumber: string;
    /** Relative asset path, or null. Same caveat as `CourseMedia`. */
    bannerImgSrc: string | null;
  };
  courseProvider: { name: string } | null;
  courseRun: {
    courseId: string;
    isStarted: boolean;
    isArchived: boolean;
    startDate: string | null;
    endDate: string | null;
    /** Deep link back into the last visited unit, or null before the first visit. */
    resumeUrl: string | null;
    homeUrl: string;
    progressUrl: string;
  };
  enrollment: {
    isEnrolled: boolean;
    mode: string;
    hasStarted: boolean;
    lastEnrolled: string | null;
    coursewareAccess: { isTooEarly: boolean; isStaff: boolean; hasUnmetPrerequisites: boolean };
  };
  gradeData: { isPassing: boolean };
  certificate: { isEarned: boolean; isDownloadable: boolean; certPreviewUrl: string | null };
}

export interface LearnerHome {
  courses: LearnerCourse[];
  platformSettings: { supportEmail: string | null };
}

export const getLearnerHome = () => openedx.get<LearnerHome>('/api/learner_home/init');

/**
 * This academy's courses out of everything the learner is enrolled in.
 *
 * Inactive enrolments are dropped as well: the API keeps a record after someone leaves a
 * course, and a dashboard that lists courses you have unenrolled from is wrong.
 */
export function myCourses(home: LearnerHome | undefined): LearnerCourse[] {
  return (home?.courses ?? []).filter(
    (entry) =>
      entry.enrollment.isEnrolled &&
      // Licence as well as org, or a learner's own dashboard would hide the licensed
      // course they are actually taking here — the one failure of this kind they would
      // report as "my course disappeared".
      visibleToTenant(entry.courseRun.courseId, entry.courseProvider?.name),
  );
}

/**
 * Every course this learner is taking in this academy — owned and licensed, one list.
 *
 * A thin wrapper now, and it used to be much more than that: `/api/learner_home/init` filters
 * the learner's enrolments through the site's org whitelist BEFORE serialising, so a licensed
 * course was gone before `myCourses` could filter, and this function rebuilt the missing
 * entries from three other endpoints. That rebuild is deleted — the same licence is now applied
 * inside the platform's own enrolment query, so the endpoint returns them. Measured on a
 * borrowing host: `[]` before, both licensed enrolments after.
 *
 * Kept as a named function rather than folded back into the pages, because "which of my courses
 * belong to this academy" is one question and three pages ask it.
 */
export async function getMyCourses(): Promise<LearnerCourse[]> {
  return myCourses(await getLearnerHome());
}

/* ------------------------------------------------------------ my progress ------ */

/**
 * Completion for every course this learner is taking in this academy. ONE request.
 *
 * `/api/learner_home/init` — the endpoint behind `getMyCourses` — carries enrolment, grade
 * pass/fail and certificate state but NO completion percentage, so anything drawing a
 * progress bar needs a second source. The platform's own answer is
 * `/api/course_home/v1/progress/{id}/`, which is per course and has no bulk equivalent;
 * `frontend-app-learning` calls it one course at a time because its page is about one
 * course. The dashboard's library grid is not, and it was making that call per card.
 *
 * `GET /api/pathways/v1/learner/progress` answers for all of them in one request, and it
 * returns THE SAME NUMBER: the server calls `get_course_blocks_completion_summary`, which is
 * the function `/api/course_home/v1/progress/{id}/` serialises as `completion_summary`, and
 * applies the same `complete / (complete + incomplete)` division this function used to do
 * here. No bar on any card moves because of this change. (The completion aggregator would
 * have been two queries rather than one read per course, and was measured calling a finished
 * course half done — the reasoning is in `learner.completion_by_course`.)
 *
 * NEVER THROWS, the same contract the per-course reader had: an unreachable course is
 * simply missing from the map, which every caller already renders as zero.
 */
export async function myCourseProgress(): Promise<Map<string, number>> {
  const data = await openedx
    .get<{ results: { course_id: string; completion: number }[] } | null>(
      '/api/pathways/v1/learner/progress',
      true,
    )
    .catch(() => null);

  return new Map((data?.results ?? []).map((row) => [row.course_id, row.completion]));
}

/* ---------------------------------------------------------------- helpers ------ */

/** Course start as the author meant it, or null when there is genuinely no date. */
export function startLabel(course: CatalogueCourse): string | null {
  // 'empty' means the author never set one; the API still returns a placeholder date in
  // `start`, and printing it would announce a start that is not real.
  if (course.start_type === 'empty') return null;
  return course.start_display ?? formatDate(course.start);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Best available image for a course card, or null to fall back to the branded tile.
 *
 * THE RELATIVE FORM FIRST, and that ordering is the whole point of this function.
 *
 * The API hands back the same asset twice: once as a path-absolute `uri`
 * (`/asset-v1:…+block@course_image.png`) and once as an absolute url the LMS builds itself
 * (`image.small`/`image.raw`, and `banner_image.uri_absolute`). The absolute form is
 * hard-coded to `http://<host>/…` — the LMS composes it from its own idea of the scheme,
 * not the scheme the visitor is on. Served over https, every one of those is mixed content:
 * the browser blocks it or silently upgrades it to a port nothing listens on, the `onError`
 * below fires, and a catalogue of real artwork renders as a grid of coloured letter tiles.
 * The relative form is same-origin, so it is correct under either scheme and the tenant's
 * own proxy already serves it. This is the same reasoning, and the same choice, as
 * `courseHeroImage`'s second narrowing — see it for the measurement.
 *
 * The asset PREFERENCE is unchanged: the card first, the banner only if there is no card.
 * `course_image.uri` and `image.small`/`image.raw` are one asset in two shapes, as are
 * `banner_image.uri` and `.uri_absolute`; each relative form simply now sits ahead of its
 * own absolute twin, which stays as a last resort for a payload that carries only that.
 *
 * The tile fallback is not optional either way. Course images are optional in Studio and
 * the API returns a url whether or not the asset was ever uploaded, so the caller's
 * `onError` still has to cover a url that does not resolve.
 */
export function courseImage(media: CourseMedia | undefined): string | null {
  return (
    media?.course_image?.uri ??
    media?.image?.small ??
    media?.image?.raw ??
    media?.banner_image?.uri ??
    media?.banner_image?.uri_absolute ??
    null
  );
}

/**
 * Best available image to put BEHIND A HEADING, or null to fall back to the brand gradient.
 *
 * NOT `courseImage`, and the difference is a bug that was on every course page: Open edX has
 * two image fields for a course and they are two different DESIGNS, not two sizes of one.
 *
 *   `course_image`   the CARD. 1280×720, and on this platform's own template the course
 *                    TITLE IS TYPESET INTO THE ARTWORK. Correct on a tile, where it is the
 *                    only label the tile has.
 *   `banner_image`   the BANNER. 1600×600, brand colour and logo, and NO title text —
 *                    because whatever renders it is expected to put a real heading on top.
 *
 * `courseImage` prefers the card, so the hero was rendering the card full-bleed under its
 * own `<h1>`: the title appeared twice, once as live text and once as a metre-wide grey
 * watermark baked into a JPEG, and neither was legible. (Measured on this install:
 * `…block@course_card.png` is 1280×720 with "Pekerja Lepas 101" set across it;
 * `…block@banner_image.png` for the same academy is 1600×600 with the logo and nothing else.)
 *
 * TWO NARROWINGS, both of which exist to guarantee the heading stays readable:
 *
 *   1. THE BANNER MUST DIFFER FROM THE CARD. 36 of this install's 100 first-page courses
 *      have `banner_image.uri === course_image.uri` — an author who uploaded one asset into
 *      both slots. That is a card, whatever field it arrived in, so it is refused and the
 *      hero keeps the brand gradient rather than the watermark.
 *   2. THE RELATIVE `uri`, NOT `uri_absolute`. The absolute form is built by the LMS as
 *      `http://<host>/…`; served over https that is mixed content the browser blocks or
 *      silently upgrades to a port nothing listens on. The relative path is same-origin and
 *      the tenant's own proxy already serves it.
 *
 * Null is an ordinary answer, not a failure: a course with no distinct banner gets the brand
 * gradient, which is what the hero is designed to look like when it has no art at all.
 */
export function courseHeroImage(media: CourseMedia | undefined): string | null {
  const banner = media?.banner_image?.uri ?? null;
  if (!banner) return null;
  // A banner that IS the card is the card. `course_image.uri` and `image.*` are the same
  // asset in different shapes, so both are compared — one of the two is absolute.
  const card = media?.course_image?.uri ?? null;
  if (card && banner === card) return null;
  const cardAbsolute = media?.image?.raw ?? media?.image?.small ?? null;
  if (cardAbsolute && cardAbsolute.endsWith(banner)) return null;
  return banner;
}

/**
 * Where "go to the course" leads.
 *
 * Our own player route, never the `homeUrl` the platform hands back — that points at the
 * legacy learning micro-frontend, which a headless install does not run.
 */
export const learnHref = (courseId: string) => `/learn/${encodeURIComponent(courseId)}`;

export const courseHref = (courseId: string) => `/courses/${encodeURIComponent(courseId)}`;
